import { AvailableTenantDtoAccess, type AvailableTenantDto } from '@/generated/api/models';

export interface GroupedTenants {
  members: AvailableTenantDto[];
  /** Tenants reachable through a data-sharing agreement, each carrying its `via` home provenance. */
  shared: AvailableTenantDto[];
}

function byNameThenId(a: AvailableTenantDto, b: AvailableTenantDto): number {
  const byName = a.name.localeCompare(b.name);
  if (byName !== 0) {
    return byName;
  }
  return a.id.localeCompare(b.id);
}

/** Sorting here keeps the switcher correct independently of the order the backend happens to return. */
export function groupTenants(tenants: AvailableTenantDto[]): GroupedTenants {
  const members = tenants.filter(tenant => tenant.access === AvailableTenantDtoAccess.member).sort(byNameThenId);
  const shared = tenants
    .filter(tenant => tenant.access === AvailableTenantDtoAccess['data-sharing'])
    .sort(byNameThenId);
  return { members, shared };
}

/**
 * When a `preferredTenantId` hint is given (e.g. the user was picked from a
 * single-tenant group in the switcher) and it is present in the accessible set,
 * that tenant wins. Otherwise — no hint, or a hint that no longer matches the
 * freshly fetched tenants — the default applies: a native (member) tenant when
 * one exists, so the user starts in their own context, otherwise the first
 * shared tenant.
 */
export function pickDefaultTenant(
  tenants: AvailableTenantDto[],
  preferredTenantId?: string,
): AvailableTenantDto | null {
  if (preferredTenantId !== undefined) {
    const preferred = tenants.find(tenant => tenant.id === preferredTenantId);
    if (preferred) {
      return preferred;
    }
  }
  const { members, shared } = groupTenants(tenants);
  return members[0] ?? shared[0] ?? null;
}
